import {ServiceBase} from "../../ServiceBase";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import IGraphyteClickstreamService, {
    GraphyteCompleteTrackingProperties, GraphyteEventChannel,
    GraphyteGameOpenData,
    GraphyteTrackingProperties, IGraphyteTracker
} from "./graphyte-clickstream.service.interface";
import {Check} from "@sparkware/app-utils";
import {IRoute} from "../../routing/route/route.interface";

const graphyteClickstreamInitializerFileName = 'graphyte-clickstream-initializer.js';

export class GraphyteClickstreamService extends ServiceBase implements IGraphyteClickstreamService{
    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.user.isAuthorized, (isAuthorized: boolean) => {
            if(!this._shouldSendGraphyteClickstreamData()){
                return;
            }

            if(isAuthorized){
                this._identifyUser();
                this._sendRegistrationEvent();
                this._sendLoginEvent();
            }else{
                this._sendTrackEvent({
                    event_name: "LOGOUT",
                    event_type: "customer"
                });
            }
        });

        this.reaction(() => this.services.routing.currentRoute,(currentRoute: IRoute) => {
            if(!this._shouldSendGraphyteClickstreamData()){
                return;
            }
            this._sendPageViewEvent(currentRoute);
        });

        this.services.sdk.events.onRegistrationCompleted(() => {
            this._registrationDate = this._getISODate();
        });
    }

    private _shouldSendGraphyteClickstreamData(): boolean {
        //Normally, whether we send these events or not should be tied to the AB test for Graphyte.
        //But now for some reason product wants us to disable Graphyte(feature + reporting), without disabling the AB Test
        //So to do that we return false here, once we turn Graphyte back on we just need to uncomment the other line.
        //return false;

        return this.services.abTestingFeatureDataProvider.areGraphyteListsEnabled;
    }

    private _scriptPromise: Promise<IGraphyteTracker>;

    private get graphyte(): Promise<IGraphyteTracker> {
        const graphyteTracker: IGraphyteTracker = this.services.window.getGlobalProperty('graphyte');

        return graphyteTracker ? Promise.resolve(graphyteTracker) : this._loadGraphyteScript();
    }

    private _loadGraphyteScript(): Promise<IGraphyteTracker> {
        if(!this._scriptPromise) {
            let source = this.services.configuration.resolveJsFilePath(graphyteClickstreamInitializerFileName);
            this._scriptPromise = this.services.sdk.loadScript(source, "graphyte-clickstream-initializer-script")
                .then(()  => this.services.window.getGlobalProperty('graphyte'));
        }
        return this._scriptPromise;
    }

    private _registrationDate: string = null;

    private _sendRegistrationEvent(): void{
        if(!this._registrationDate){
            return;
        }
        this._sendTrackEvent({
            event_name: "REGISTRATION",
            event_type: "customer",
            event_datetime: this._registrationDate,
            event_info_2: this.services.language.currentLanguage
        });
    }

    private _identifyUser(){
        this.graphyte.then(graphyte => graphyte.identify(this.services.user.cid));
    }

    private _sendLoginEvent(){
        this._sendTrackEvent({
            event_name: "LOGIN",
            event_type: "customer",
            event_info_2: this.services.language.currentLanguage,
            event_info_3: Boolean(this._registrationDate).toString() // if it's first login or not
        });

        this._registrationDate = null;
    }

    private _sendTrackEvent(properties: GraphyteTrackingProperties): void {
        this.graphyte.then(graphyte => graphyte.track(properties.event_name, this._buildGraphyteTrackingProperties(properties)));
    }

    private _getISODate(): string {
        return new Date(this.services.time.now()).toISOString();
    }

    private _getEventChannel(): GraphyteEventChannel {
        if(this.services.device.isTablet){
            return "tablet"
        }
        return this.services.device.os.isMobileOS ? "mobile" : "desktop";
    }

    private _buildGraphyteTrackingProperties(properties: GraphyteTrackingProperties): GraphyteCompleteTrackingProperties {
        let result: any = properties;
        result.event_datetime = properties.event_datetime || this._getISODate();
        result.brandkey = this.services.sdk.getBrandID();
        result.userid = this.services.user.cid;
        result.event_country = this.services.sdk.getCountryName();
        result.event_platform = this.services.window.location.origin;
        result.event_language = this.services.language.currentLanguage;
        result.event_channel = this._getEventChannel();
        result.event_uuid = this.services.tools.getGuid();
        return result;
    }

    private _sendPageViewEvent(currentRoute: IRoute): void {
        let arenaType = currentRoute?.routeDefinition.data?.arenaType || '';
        let properties: GraphyteTrackingProperties = {
            event_name: "PAGE_VIEW",
            event_type: "browse",
            event_info_2: this.services.sdk.getCountryName(),
            event_info_3: this.services.language.currentLanguage
        };

        this.graphyte.then(graphyte => graphyte.page("", arenaType, this._buildGraphyteTrackingProperties(properties)));
    }

    public sendGameOpenEvent(data: GraphyteGameOpenData): void {
        if(!this._shouldSendGraphyteClickstreamData()){
            return;
        }

        let location_index = Check.isNumber(data.gameIndex) ? (data.gameIndex + 1) : 0;
        this._sendTrackEvent({
            event_name: 'GAME_PLAY',
            event_type: "gaming",
            event_value: data.isReal.toString(),
            event_info_1: data.gameType,
            event_info_2: data.gameTitle,
            event_info_3: data.gameCategory,
            event_location: data.listTitle,
            event_location_index: location_index
        });
    }
}